import React from "react";
import { Filter, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

/** Aktif (seçili) bir filtre kutusunun vurgusu — kapalı olandan bakışta ayrılsın. */
const ACTIVE_FIELD_CLS =
  "border-blue-500 bg-blue-50 font-semibold text-blue-900 ring-1 ring-blue-200";

/**
 * Detay modallarının filtre state'i için boş/başlangıç değeri.
 *
 * Tek kaynak: modalı açan her `show*Detail` handler'ı bunu kullanır, böylece
 * bir modalda bırakılan filtre (özellikle serbest metin araması) bir sonraki
 * modala taşınıp "park bulunamadı" gibi görünmez.
 */
export const EMPTY_DETAIL_FILTERS = {
  q: "",
  region: "all",
  formen: "all",
  bolgeMuh: "all",
  mudurYrd: "all",
};

/**
 * Detay modallarındaki organizasyon filtresi çubuğu (kademeli / cascading).
 *
 * Park adı için serbest metin araması + Bölge / Formen / Bölge Mühendisi /
 * Müdür Yardımcısı için kendi içinde aranabilir 4 dropdown render eder.
 * Seçenek listeleri çağıran taraftan (kademeli olarak) hesaplanıp props ile
 * verilir; park adı araması da bu kademeye dahildir (çağıran taraftaki
 * `parkPassesOrgFilter`), yani yazdıkça dropdown seçenekleri de daralır.
 *
 * `children`: dropdown'lardan sonra, "Temizle" linkinden önce render edilir
 * (örn. zemin için çoklu-seçim dropdown'u buraya konur).
 */
export default function DetailOrgFilterBar({
  filters,
  setFilters,
  regionOptions = [],
  formenOptions = [],
  bolgeMuhOptions = [],
  mudurYrdOptions = [],
  children,
  // `children` ile gelen ek filtre (örn. zemin) aktif mi? "Temizle" linki bunu
  // da hesaba katar ve temizlerken onClearExtra çağrılır.
  extraActive = false,
  onClearExtra,
  // Arama kutusu park dışında da kullanılıyor (ör. izinli personel listesi),
  // o yüzden ne aradığı çağıran tarafça söylenir.
  searchPlaceholder = "Park adı ara...",
  searchAriaLabel = "Park adı ara",
}) {
  const f = filters;
  const query = f.q || "";
  const selectCls = (active) =>
    cn("h-8 text-xs flex-1 min-w-[130px]", active && ACTIVE_FIELD_CLS);
  const orgActive =
    query.trim() !== "" ||
    f.region !== "all" ||
    f.formen !== "all" ||
    f.bolgeMuh !== "all" ||
    f.mudurYrd !== "all";
  const hasActiveFilter = orgActive || extraActive;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Filter className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="relative flex-1 min-w-[150px]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          className={cn(
            "h-8 w-full rounded-md border border-input bg-background pl-7 pr-7 text-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            query !== "" && ACTIVE_FIELD_CLS
          )}
        />
        {query !== "" && (
          <button
            type="button"
            onClick={() => setFilters((s) => ({ ...s, q: "" }))}
            aria-label="Aramayı temizle"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <SearchableSelect
        value={f.region}
        onChange={(v) => setFilters((s) => ({ ...s, region: v }))}
        options={regionOptions}
        allLabel="Tüm Bölgeler"
        searchPlaceholder="Bölge ara..."
        className={selectCls(f.region !== "all")}
      />
      <SearchableSelect
        value={f.formen}
        onChange={(v) => setFilters((s) => ({ ...s, formen: v }))}
        options={formenOptions}
        allLabel="Tüm Formenler"
        searchPlaceholder="Formen ara..."
        className={selectCls(f.formen !== "all")}
      />
      <SearchableSelect
        value={f.bolgeMuh}
        onChange={(v) => setFilters((s) => ({ ...s, bolgeMuh: v }))}
        options={bolgeMuhOptions}
        allLabel="Tüm Bölge Müh."
        searchPlaceholder="Bölge mühendisi ara..."
        className={selectCls(f.bolgeMuh !== "all")}
      />
      <SearchableSelect
        value={f.mudurYrd}
        onChange={(v) => setFilters((s) => ({ ...s, mudurYrd: v }))}
        options={mudurYrdOptions}
        allLabel="Tüm Müdür Yrd."
        searchPlaceholder="Müdür yardımcısı ara..."
        className={selectCls(f.mudurYrd !== "all")}
      />
      {children}
      {hasActiveFilter && (
        <button
          type="button"
          onClick={() => {
            setFilters({ ...EMPTY_DETAIL_FILTERS });
            if (onClearExtra) onClearExtra();
          }}
          className="text-xs text-blue-600 hover:underline shrink-0"
        >
          Temizle
        </button>
      )}
    </div>
  );
}

/**
 * Detay modallarında "hiç park kalmadı" durumu.
 *
 * Sadece "bulunamadı" demek yanıltıcıydı: kullanıcı biraz önce seçtiği bölge
 * mühendisini unutup park adı arayınca, park sistemde VARKEN yokmuş gibi
 * görünüyordu. Bu yüzden hangi filtrelerin açık olduğu tek tek yazılır ve tek
 * tıkla temizleme sunulur.
 */
export function DetailFilterEmptyState({
  filters,
  setFilters,
  regionOptions = [],
  formenOptions = [],
  bolgeMuhOptions = [],
  mudurYrdOptions = [],
  extraActive = false,
  extraLabel,
  onClearExtra,
  // Liste park dışında bir şey olabilir (ör. izinli personel).
  noun = "park",
  searchChipLabel = "Park adı",
}) {
  const f = filters;
  const nameOf = (opts, id) =>
    opts.find((o) => String(o.id) === String(id))?.name || "seçili";

  const chips = [];
  if ((f.q || "").trim() !== "") chips.push(`${searchChipLabel}: "${f.q.trim()}"`);
  if (f.region !== "all") chips.push(`Bölge: ${nameOf(regionOptions, f.region)}`);
  if (f.formen !== "all") chips.push(`Formen: ${nameOf(formenOptions, f.formen)}`);
  if (f.bolgeMuh !== "all")
    chips.push(`Bölge Müh.: ${nameOf(bolgeMuhOptions, f.bolgeMuh)}`);
  if (f.mudurYrd !== "all")
    chips.push(`Müdür Yrd.: ${nameOf(mudurYrdOptions, f.mudurYrd)}`);
  if (extraActive) chips.push(extraLabel || "Ek filtre açık");

  return (
    <div className="text-center py-8">
      <p className="text-gray-500">Seçilen filtrelere uygun {noun} bulunamadı.</p>
      {chips.length > 0 && (
        <>
          <p className="text-xs text-gray-400 mt-2">
            Şu filtreler açık — kayıt listede olsa bile bunlar yüzünden
            görünmüyor olabilir:
          </p>
          <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
            {chips.map((c) => (
              <span
                key={c}
                className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
              >
                {c}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setFilters({ ...EMPTY_DETAIL_FILTERS });
              if (onClearExtra) onClearExtra();
            }}
            className="mt-3 text-xs text-blue-600 hover:underline"
          >
            Filtreleri temizle
          </button>
        </>
      )}
    </div>
  );
}
